import type { CommencementBundle } from "~/composables/useCommencement";
import { isStaffRole, normalizeUserRole } from "~/utils/userRoles";

export type UserRoleProfile = {
  id?: string;
  user_id?: string;
  email?: string;
  role?: string;
  name?: string;
  avatar_url?: string | null;
};

let fetchGeneration = 0;
let profileFetchInFlight: Promise<void> | null = null;
let profileFetchUserId: string | null = null;

export const useUserRole = () => {
  const supabase = useSupabaseClient();
  const user = useSupabaseUser();

  const profile = useState<UserRoleProfile | null>("userRole:profile", () => null);
  const loading = useState<boolean>("userRole:loading", () => false);
  const error = useState<string | null>("userRole:error", () => null);

  const isAdmin = computed(() => normalizeUserRole(profile.value?.role) === "admin");
  const isDev = computed(() => normalizeUserRole(profile.value?.role) === "dev");
  const isOwner = computed(() => normalizeUserRole(profile.value?.role) === "owner");
  const isDesigner = computed(
    () => normalizeUserRole(profile.value?.role) === "designer",
  );
  const isAdminDev = computed(
    () => normalizeUserRole(profile.value?.role) === "admindev",
  );
  const isMember = computed(() => normalizeUserRole(profile.value?.role) === "member");

  const isClient = computed(() => {
    const role = normalizeUserRole(profile.value?.role);
    return role === "client" || role === "user" || role === "member";
  });

  /** True when user has staff-level access: admin, dev, owner, or adminDev. */
  const isStaff = computed(() => isStaffRole(profile.value?.role));


  /** True when user can manage enquiries (staff only). */
  const canManageEnquiries = computed(() => isStaff.value);

  const clearProfile = () => {
    profile.value = null;
    error.value = null;
    fetchGeneration += 1;
    profileFetchInFlight = null;
    profileFetchUserId = null;
    clearMiddlewareAuthProfileCache();

    const bundle = useState<CommencementBundle | null>("commencementBundle", () => null);
    bundle.value = null;
  };

  const resolveAuthSession = async (): Promise<{
    userId?: string;
    email?: string | null;
  }> => {
    let userId = user.value?.id;
    let email = user.value?.email ?? null;
    if (!userId) {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      userId = authUser?.id;
      email = authUser?.email ?? email;
    }
    return { userId, email };
  };

  const fetchProfile = async (options?: { force?: boolean }) => {
    const { userId, email } = await resolveAuthSession();

    if (!userId) {
      clearProfile();
      return;
    }

    if (
      !options?.force &&
      profile.value?.user_id === userId &&
      profile.value?.role
    ) {
      return;
    }

    if (
      profile.value?.user_id &&
      profile.value.user_id !== userId &&
      profile.value?.email?.toLowerCase() !== email?.toLowerCase()
    ) {
      profile.value = null;
    }

    if (!options?.force && profileFetchInFlight && profileFetchUserId === userId) {
      return profileFetchInFlight;
    }

    const gen = ++fetchGeneration;
    loading.value = true;
    error.value = null;

    const run = async () => {
      try {
        let resolved =
          (await fetchAuthProfileFromApi(supabase)) ??
          (await resolveAuthProfile(supabase, userId, email));

        if (gen !== fetchGeneration) return;

        if (!resolved) {
          profile.value = null;
          return;
        }

        if (!resolved.user_id || resolved.user_id !== userId) {
          await syncAuthProfileUserId(supabase, resolved, userId, email);
          resolved = { ...resolved, user_id: userId };
        }

        profile.value = resolved;

        const middlewareCache = useState<UserRoleProfile | null | undefined>(
          "middleware:authProfile",
          () => undefined,
        );
        middlewareCache.value = resolved;
      } catch (e: unknown) {
        if (gen !== fetchGeneration) return;
        const message = e instanceof Error ? e.message : "Unknown error";
        console.error("[useUserRole] fetchProfile exception:", message);
        error.value = message;
        profile.value = null;
      } finally {
        if (gen === fetchGeneration) loading.value = false;
      }
    };

    profileFetchUserId = userId;
    profileFetchInFlight = run().finally(() => {
      if (profileFetchUserId === userId) {
        profileFetchInFlight = null;
        profileFetchUserId = null;
      }
    });

    return profileFetchInFlight;
  };

  return {
    profile,
    loading,
    error,
    isAdmin,
    isDev,
    isOwner,
    isDesigner,
    isAdminDev,
    isClient,
    isMember,
    isStaff,
    canManageEnquiries,
    clearProfile,
    fetchProfile,
  };
};
